import { substituteAmount } from './discountMessage';

const asNumber = (value) => parseFloat(value || 0);

const asDisplayString = (value, preferredKeys = []) => {
  if (value == null) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    for (const key of preferredKeys) {
      const candidate = value[key];
      if (candidate != null && candidate !== '') return String(candidate);
    }
  }
  return '';
};

const formatPaymentLabel = (mode = '') => {
  const trimmed = String(mode || '').trim();
  return trimmed || 'Payment';
};

const buildGstSummary = (items = [], order = {}) => {
  const rows = new Map();

  items.forEach((item) => {
    const key = `${item.cgst_rate || 0}|${item.sgst_rate || 0}|${item.igst_rate || 0}`;
    const existing = rows.get(key) || {
      label: item.igst_rate && asNumber(item.igst_rate) > 0
        ? `IGST ${asNumber(item.igst_rate).toFixed(0)}%`
        : `GST ${(asNumber(item.cgst_rate) + asNumber(item.sgst_rate)).toFixed(0)}%`,
      taxableAmount: 0,
      cgstAmount: 0,
      sgstAmount: 0,
      igstAmount: 0,
    };

    const lineTotal = asNumber(item.line_total);
    const taxAmount = asNumber(item.tax_amount);
    existing.taxableAmount += Math.max(0, lineTotal - taxAmount);
    existing.cgstAmount += asNumber(item.cgst_amount);
    existing.sgstAmount += asNumber(item.sgst_amount);
    existing.igstAmount += asNumber(item.igst_amount);
    rows.set(key, existing);
  });

  const summaryRows = Array.from(rows.values());
  if (summaryRows.length > 0) return summaryRows;

  return [{
    label: order.is_intrastate ? 'GST' : 'IGST',
    taxableAmount: Math.max(0, asNumber(order.subtotal) - asNumber(order.discount_total)),
    cgstAmount: asNumber(order.cgst_total),
    sgstAmount: asNumber(order.sgst_total),
    igstAmount: asNumber(order.igst_total),
  }];
};

// `template` is the resolved ReceiptTemplate from GET /api/v1/pos/receipt_template
// (see useGetReceiptTemplateQuery) -- undefined/null only in the brief window
// before that query first resolves (it's prefetched at login in AuthInitializer,
// so in practice this window is over well before checkout/print happens). No
// hardcoded fallback content here on purpose: showing this cashier's actual
// (still-loading) org data, even briefly blank, beats silently showing some
// other org's or a generic default's T&C as if it were real.
export const mapOrderToReceipt = (payload, template) => {
  const order = payload?.data?.order || payload?.order || payload;
  if (!order) return null;

  const discountDisplayMode = template?.discount_display_mode || 'amount';
  const savingsMessageTemplate = template?.savings_message_template || '';
  const isSavingsMode = discountDisplayMode === 'savings_message';

  const items = (order.items || []).map((item, index) => {
    const discountAmount = asNumber(item.total_discount_amount || item.discount_amount || 0);
    return {
      index: index + 1,
      name: item.product_name || 'Item',
      color: item.product_color || '',
      size: item.product_size || '',
      barcode: asDisplayString(item.barcode || item.product_barcode, ['code', 'value', 'name']),
      hsn: asDisplayString(item.hsn_code || item.hsn, ['code', 'value', 'name', 'description']),
      quantity: asNumber(item.quantity),
      rate: asNumber(item.unit_price),
      amount: asNumber(item.line_total),
      discountAmount,
      discountLabel: item.discount_name || item.promotion_name || '',
      // Precomputed once here so every renderer (HTML, PDF, ESC-POS) shows the
      // exact same text without re-implementing the {amount} substitution.
      savingsMessage: discountAmount > 0 && isSavingsMode
        ? substituteAmount(savingsMessageTemplate, discountAmount)
        : null,
    };
  });

  const payments = (order.payments || [])
    .filter((payment) => asNumber(payment.amount) > 0)
    .map((payment) => ({
      label: formatPaymentLabel(payment.payment_mode),
      amount: asNumber(payment.amount),
      referenceNumber: payment.reference_number || '',
      // Gift voucher tender: masked code + the balance left after this bill.
      // ASCII only and under 40 chars — thermal printers can't print "₹" and fit 42 chars a line.
      ...(payment.gift_voucher
        ? { note: `${payment.gift_voucher.masked_code} | Bal Rs.${asNumber(payment.gift_voucher.balance).toFixed(2)}` }
        : {}),
    }));

  const discount = asNumber(order.discount_total);
  const billDiscountAmount = asNumber(order.bill_discount_amount);
  const billPromotionDiscountAmount = asNumber(order.bill_promotion_discount_amount);
  // Only the plain bill-level "Discount" rollup row (shown when there's no
  // separate bill/promotion discount) gets the savings-message treatment --
  // Bill Discount and Promotion Discount are distinct, deliberately-applied
  // discounts, not a rollup of item discounts, and stay as amounts. Matches
  // apex_v3's docs/features/pos-receipt-template/overview.md Business Rules.
  const billSavingsMessage = discount > 0 && billDiscountAmount <= 0 && billPromotionDiscountAmount <= 0 && isSavingsMode
    ? substituteAmount(savingsMessageTemplate, discount)
    : null;

  return {
    orderId: order.id,
    billNumber: order.order_number || order.id,
    invoiceTitle: 'TAX INVOICE',
    createdAt: order.paid_at || order.created_at,
    storeName: order.store?.name || order.store_name || order.organization?.name || '',
    storeAddress: order.store?.address || '',
    storePhone: order.store?.phone_number || order.organization?.phone_number || '',
    storeEmail: order.store?.email || order.organization?.email || '',
    organizationName: order.organization?.name || '',
    gstin: order.organization?.gstin || '',
    cashierName: order.cashier_name || '',
    salespersonName: order.salesperson_name || '',
    customerName: order.customer_name || '',
    customerMobile: order.customer_phone_number || order.customer_mobile || '',
    customerAddress: order.customer_address || '',
    subtotal: asNumber(order.subtotal),
    discount,
    billSavingsMessage,
    billDiscountAmount,
    billDiscountLabel: order.bill_discount_name || '',
    billPromotionDiscountAmount,
    billPromotionDiscountLabel: order.bill_promotion_name || '',
    totalAmount: asNumber(order.grand_total),
    roundingAdjustment: asNumber(order.rounding_adjustment),
    loyaltyRedemption: asNumber(order.loyalty_redemption_amount),
    loyaltyPointsEarned: asNumber(order.loyalty_points_earned),
    loyaltyPointsBalance: order.customer_loyalty_points_balance != null
      ? asNumber(order.customer_loyalty_points_balance)
      : null,
    totalPaid: asNumber(order.amount_paid),
    changeDue: asNumber(order.change_due),
    payments,
    items,
    gstSummary: buildGstSummary(order.items || [], order),
    // Receipt template fields -- see apex_v3 docs/features/pos-receipt-template/overview.md
    terms: template?.terms || [],
    footerMessage: template?.footer_message || '',
    websiteHandle: template?.website_handle || '',
    instagramHandle: template?.instagram_handle || '',
    declarationText: template?.declaration_text || '',
    logoUrl: template?.logo_url || '',
    showTotalPaidRow: template?.show_total_paid_row !== false,
    rateColumnLabel: template?.rate_column_label || 'Rate',
    amountColumnLabel: template?.amount_column_label || 'Amt',
    showRateColumn: template?.show_rate_column !== false,
    showAmountColumn: template?.show_amount_column !== false,
    itemAmountDecimalPlaces: template?.item_amount_decimal_places || 2,
    receiptFontSize: template?.receipt_font_size || 'normal',
  };
};
